import React, { lazy, Suspense } from "react";

import { graphql } from "gatsby";

import VariantHelper from "./utils/variantHelper";
import Button from "../components/button/button";
import IconButton from "../components/button/iconButton";
import CopyToClipboard from "../components/copyToClipboard/copyToClipboard";
import iconHelper from "../utils/iconHelper";

interface KontentItemCtaButtonProps {
  data: Queries.kontent_item_cta_button;
  onClick?: () => void;
}

// without this lazy loading, production build would fail with unspecific error.
const KontentItemSspContactForm = lazy(() => (
  import("./kontentItemSspContactForm")
));

const getUrlParameters = (query: string) => {
  let urlParameter;

  try {
    const queryParameters = new URLSearchParams(query);
    const parameters: string[] = [];

    queryParameters.forEach((value, key) => {
      if (["urn", "utm_source", "utm_medium", "utm_content", "utm_campaign"].includes(key)) {
        parameters.push(`${key}=${value}`);
      }
    });

    urlParameter = parameters.length > 0 ? `?${parameters.join("&")}` : "";
  } catch {
    urlParameter = "";
  }

  return urlParameter;
};

const KontentItemCtaButton: React.FC<KontentItemCtaButtonProps> = ({ data, onClick = () => {} }) => {
  if (!data || !data.elements) {
    return null;
  }

  const elements = data.elements!;
  // const isSspContactForm = buttonUrlIsNotEmpty && button.elements.button_url.value[0]
  //  ?.elements?.ssp_contact?.value.length > 0;

  let clipboard = (elements.button_clipboard?.value?.length ?? 0) > 0;

  const variant = VariantHelper(
    elements.button_style_color?.value?.[0]?.codename,
    elements.button_style_variant?.value?.[0]?.codename,
  );

  const tmpChildren = [elements.button_text?.value];

  if ((elements.icon?.value?.length ?? 0) > 0) {
    tmpChildren.unshift(iconHelper(elements.icon?.value?.[0]?.codename));
  }

  const buttonProps = {
    children: tmpChildren,
    href: (elements.button_url?.value?.length ?? 0) > 0
      ? elements.button_url?.value?.[0]?.id
      : elements.button_url_ext?.value,
    onClick,
  };

  const parameterOrFragment = elements.url_paramter_or_fragment?.value;

  if (typeof parameterOrFragment === "string") {
    const url = new URL(process.env.GATSBY_SITE_URL + parameterOrFragment);
    const { search, hash } = url;

    if (hash.indexOf("?") !== -1) {
      const hash2 = hash.split("?");

      buttonProps.urlParameter = getUrlParameters(hash2[1]) + hash2[0];
    } else {
      buttonProps.urlParameter = getUrlParameters(search) + hash;
    }
  }

  if (elements.button_mailto?.value) {
    buttonProps.href = `mailto:${elements.button_mailto.value}`;
    clipboard = false;
  }

  if (elements.button_phone?.value) {
    clipboard = false;
    buttonProps.href = `tel:${elements.button_phone.value.replace(/[^0-9]/g, "")}`;
  }

  if ((elements.ssp_contact_form?.value?.length ?? 0) > 0) {
    return (
      <Suspense fallback={<div />}>
        <KontentItemSspContactForm
          variant={variant}
          buttonText={elements.button_text?.value || ""}
          overwriteButton={(elements.icon?.value?.length ?? 0) > 0}
          buttonProps={buttonProps}
        />
      </Suspense>
    );
  }

  if (clipboard && buttonProps.href?.length === 0) {
    return (
      <CopyToClipboard
        textToCopy={elements.button_clipboard?.value || ""}
      >
        {(elements.icon?.value?.length ?? 0) > 0
          ? (
            <IconButton
              variant={variant}
              {...buttonProps}
            />
          )
          : (
            <Button
              variant={variant}
              {...buttonProps}
            />
          )}
      </CopyToClipboard>
    );
  }

  if ((elements.icon?.value?.length ?? 0) > 0) {
    return (
      <IconButton
        variant={variant}
        {...buttonProps}
      />
    );
  }

  return (
    <Button
      variant={variant}
      {...buttonProps}
    />
  );
};

export const query = graphql`
  fragment KontentItemCtaButton on kontent_item_cta_button {
    id
    system {
      codename
    }
    elements {
      button_text {
        value
      }
      button_clipboard {
        value
      }
      button_url {
        value {
          id
          ... on kontent_item_form {
            elements {
              ssp_contact {
                value {
                  codename
                }
              } 
            }
          }
        }
      }
      button_url_ext {
        value
      }
      icon {
        value {
          codename
        }
      }
      button_style_color {
        value {
          codename
        }
      }
      button_style_variant {
        value {
          codename
        }
      }
      button_mailto {
        value
      }
      button_phone {
        value
      }
      ssp_contact_form {
        value {
          codename
        }
      }
      url_paramter_or_fragment {
        value
      }
    }
  }
`;

export default KontentItemCtaButton;
